import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '@/components/common/Spinner';
import {
  Download,
  Monitor,
  ChevronDown,
  ChevronRight,
  Clock,
  GitCommit,
  HardDrive,
  CheckCircle,
  Zap,
  Shield,
  Bot,
  MessageSquare,
  ArrowRight,
} from 'lucide-react';
import * as releasesApi from '@/api/releases';
import { useCdnToken } from '@/hooks/useCdnToken';

const FONT_FAMILY = "'Aeonik Fono', 'Inter', system-ui, sans-serif";
const SLACK_FEEDBACK_URL = 'https://tubi.enterprise.slack.com/archives/C0A6EGKEUK1';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatRelativeDate(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return formatDate(iso);
}

function AppleLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11" />
    </svg>
  );
}

function TdaIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" fill="#FFDE00" rx="4" />
      <text
        x="16"
        y="22"
        fontFamily="system-ui"
        fontSize="18"
        fontWeight="bold"
        textAnchor="middle"
        fill="#000"
      >
        T
      </text>
    </svg>
  );
}

const FEATURES = [
  {
    icon: Zap,
    title: 'Native Performance',
    description:
      'Built with Rust and Tauri for instant startup and minimal memory footprint. No Electron overhead.',
  },
  {
    icon: Shield,
    title: 'Signed & Notarized',
    description:
      'Apple-notarized and code-signed so macOS trusts it from day one. No Gatekeeper warnings.',
  },
  {
    icon: Bot,
    title: 'AI-Orchestrated Workflows',
    description:
      'Chain AI agents through configurable workflows — from requirements to PR — with isolated worktrees and full audit trails.',
  },
];

const INSTALL_STEPS = [
  { step: '1', text: 'Open the downloaded .dmg file' },
  { step: '2', text: 'Drag TDA into your Applications folder' },
  { step: '3', text: 'Launch TDA from Applications or Spotlight' },
];

export default function DownloadsPage() {
  const [releases, setReleases] = useState<releasesApi.ReleaseEntry[]>([]);
  const [tempBuilds, setTempBuilds] = useState<releasesApi.TempBuildEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPrevious, setShowPrevious] = useState(false);
  const [showTemp, setShowTemp] = useState(false);
  const { signUrl, ready: cdnReady } = useCdnToken();

  useEffect(() => {
    async function load() {
      try {
        const [releasesRes, tempRes] = await Promise.all([
          releasesApi.listReleases(),
          releasesApi.listTempBuilds(),
        ]);
        setReleases(releasesRes.releases);
        setTempBuilds(tempRes.builds);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load releases');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const latest = releases[0];
  const previous = releases.slice(1);
  const downloadTarget = latest || tempBuilds[0];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: FONT_FAMILY }}>
      {/* ─── Header ─── */}
      <header className="border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 h-12 flex items-center justify-between">
          <Link
            to="/downloads"
            className="flex items-center gap-2 text-[var(--md-graphite)] hover:text-[var(--md-graphite)]"
            style={{ fontFamily: FONT_FAMILY }}
          >
            <span className="text-sm font-medium">TDA</span>
          </Link>
          <div className="flex items-center gap-4">
            <a
              href={SLACK_FEEDBACK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[var(--md-slate)] hover:text-[var(--md-graphite)] transition-colors"
              style={{ fontFamily: FONT_FAMILY }}
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Feedback
            </a>
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-xs text-[var(--md-slate)] hover:text-[var(--md-graphite)] transition-colors"
              style={{ fontFamily: FONT_FAMILY }}
            >
              Sandbox
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </header>

      {/* ─── Hero Section (two-column) ─── */}
      <section className="max-w-5xl mx-auto px-6 pt-16 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Left: Copy + CTA */}
          <div>
            <TdaIcon className="w-14 h-14 mb-5" />
            <h1
              className="text-5xl text-[var(--md-graphite)] mb-3 normal-case tracking-normal"
              style={{ fontFamily: FONT_FAMILY, fontWeight: 200 }}
            >
              TDA for macOS
            </h1>
            <p
              className="text-lg text-[var(--md-slate)] leading-relaxed mb-8 max-w-lg"
              style={{ fontFamily: FONT_FAMILY }}
            >
              The desktop companion for Tubi Development Automation. Orchestrate AI
              workflows, manage isolated worktrees, and ship features from requirements to PR.
            </p>

            {/* Download CTA */}
            {downloadTarget ? (
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => window.location.assign(signUrl(downloadTarget.artifact.url))}
                  className="inline-flex items-center justify-center rounded-full border-0 bg-[var(--md-sunbeam)] px-8 py-3 text-base font-medium text-[var(--md-graphite)] transition-all hover:bg-[var(--md-sunbeam-dark)] hover:shadow-sm cursor-pointer w-fit"
                  style={{ fontFamily: FONT_FAMILY }}
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download for Mac
                </button>
                <div
                  className="flex items-center gap-3 text-sm text-[var(--md-slate)]"
                  style={{ fontFamily: FONT_FAMILY }}
                >
                  <span className="inline-flex items-center gap-1">
                    <AppleLogo className="w-3.5 h-3.5" />
                    Apple Silicon
                  </span>
                  <span className="text-gray-300">|</span>
                  <span>{formatBytes(downloadTarget.artifact.size)}</span>
                  {latest && (
                    <>
                      <span className="text-gray-300">|</span>
                      <span>v{latest.version}</span>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div
                className="px-6 py-3 border border-gray-200 rounded-full text-[var(--md-slate)] w-fit"
                style={{ fontFamily: FONT_FAMILY }}
              >
                No builds available yet. Check back soon.
              </div>
            )}
          </div>

          {/* Right: Screenshot */}
          <div className="flex items-center justify-center">
            {cdnReady && (
              <img
                src={signUrl("/protected/portal-assets/tda-screenshot.png")}
                alt="TDA desktop app"
                className="w-full max-w-md rounded-lg"
                loading="eager"
              />
            )}
          </div>
        </div>
      </section>

      {/* ─── See TDA in Action (Video Demo) ─── */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2
          className="text-2xl text-[var(--md-graphite)] mb-3 text-center normal-case tracking-normal"
          style={{ fontFamily: FONT_FAMILY, fontWeight: 200 }}
        >
          See TDA in Action
        </h2>
        <p
          className="text-[var(--md-slate)] text-center max-w-2xl mx-auto mb-10 leading-relaxed"
          style={{ fontFamily: FONT_FAMILY }}
        >
          Watch a full walkthrough of TDA — from onboarding and tool setup to running AI-orchestrated
          workflows that take you from requirements to pull request.
        </p>
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
            {cdnReady && (
              <video
                className="w-full aspect-video"
                controls
                playsInline
                preload="metadata"
                poster={signUrl("/protected/portal-assets/tda-demo-poster.jpg")}
              >
                <source src={signUrl("/protected/portal-assets/tda-demo.mp4")} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-5xl mx-auto px-6">
        <div className="h-px bg-gray-200" />
      </div>

      {error && (
        <div className="max-w-5xl mx-auto px-6 mt-6">
          <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg text-sm">
            {error}
          </div>
        </div>
      )}

      {/* ─── Features Section ─── */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2
          className="text-2xl text-[var(--md-graphite)] mb-3 text-center normal-case tracking-normal"
          style={{ fontFamily: FONT_FAMILY, fontWeight: 200 }}
        >
          Why TDA Desktop?
        </h2>
        <p
          className="text-[var(--md-slate)] text-center max-w-2xl mx-auto mb-12 leading-relaxed"
          style={{ fontFamily: FONT_FAMILY }}
        >
          An AI-powered development orchestration system that automates the entire lifecycle — from
          requirements to pull request — while keeping humans in control.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {FEATURES.map((f) => (
            <div key={f.title} className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-[var(--md-sunbeam)]/15 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-[var(--md-graphite)]" />
              </div>
              <h3
                className="text-base font-medium text-[var(--md-graphite)] mb-2 normal-case tracking-normal"
                style={{ fontFamily: FONT_FAMILY }}
              >
                {f.title}
              </h3>
              <p
                className="text-sm text-[var(--md-slate)] leading-relaxed"
                style={{ fontFamily: FONT_FAMILY }}
              >
                {f.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-5xl mx-auto px-6">
        <div className="h-px bg-gray-200" />
      </div>

      {/* ─── Installation Steps ─── */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2
          className="text-2xl text-[var(--md-graphite)] mb-10 text-center normal-case tracking-normal"
          style={{ fontFamily: FONT_FAMILY, fontWeight: 200 }}
        >
          Get started in seconds
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
          {INSTALL_STEPS.map((s) => (
            <div key={s.step} className="flex flex-col items-center text-center gap-3">
              <div
                className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-[var(--md-graphite)] font-medium text-lg"
                style={{ fontFamily: FONT_FAMILY }}
              >
                {s.step}
              </div>
              <p
                className="text-sm text-[var(--md-ink)] leading-relaxed"
                style={{ fontFamily: FONT_FAMILY }}
              >
                {s.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-5xl mx-auto px-6">
        <div className="h-px bg-gray-200" />
      </div>

      {/* ─── All Downloads Section ─── */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2
          className="text-2xl text-[var(--md-graphite)] mb-8 text-center normal-case tracking-normal"
          style={{ fontFamily: FONT_FAMILY, fontWeight: 200 }}
        >
          All Downloads
        </h2>

        {/* Latest Release */}
        {latest && (
          <div className="mb-6">
            <div className="border border-gray-200 rounded-lg p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className="font-medium text-[var(--md-graphite)]"
                        style={{ fontFamily: FONT_FAMILY }}
                      >
                        v{latest.version}
                      </span>
                      <span
                        className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 bg-green-50 text-green-600 rounded-full"
                        style={{ fontFamily: FONT_FAMILY }}
                      >
                        Latest
                      </span>
                    </div>
                    <div
                      className="flex items-center gap-3 text-xs text-[var(--md-slate)] mt-1"
                      style={{ fontFamily: FONT_FAMILY }}
                    >
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(latest.date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <HardDrive className="w-3 h-3" />
                        {formatBytes(latest.artifact.size)}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => window.location.assign(signUrl(latest.artifact.url))}
                  className="inline-flex items-center rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-[var(--md-ink)] hover:bg-[var(--md-fog)] transition-all cursor-pointer bg-white"
                  style={{ fontFamily: FONT_FAMILY }}
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  Download
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Previous Versions */}
        {previous.length > 0 && (
          <div className="mb-6">
            <button
              type="button"
              onClick={() => setShowPrevious(!showPrevious)}
              className="flex items-center gap-2 text-sm font-medium text-[var(--md-graphite)] hover:text-[var(--md-slate)] transition-colors mb-3 normal-case tracking-normal cursor-pointer bg-transparent border-0 p-0"
              style={{ fontFamily: FONT_FAMILY }}
            >
              {showPrevious ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              Previous Releases ({previous.length})
            </button>
            {showPrevious && (
              <div className="border border-gray-200 rounded-lg p-5">
                <div className="divide-y divide-gray-100">
                  {previous.map((r) => (
                    <div
                      key={r.version}
                      className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                    >
                      <div style={{ fontFamily: FONT_FAMILY }}>
                        <span className="font-medium text-[var(--md-graphite)] text-sm">
                          v{r.version}
                        </span>
                        <span className="text-xs text-[var(--md-slate)] ml-3">
                          {formatDate(r.date)} &middot; {formatBytes(r.artifact.size)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => window.location.assign(signUrl(r.artifact.url))}
                        className="inline-flex items-center rounded-full border border-gray-300 px-3 py-1.5 text-xs font-medium text-[var(--md-ink)] hover:bg-[var(--md-fog)] transition-all cursor-pointer bg-white"
                        style={{ fontFamily: FONT_FAMILY }}
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Development Builds */}
        {tempBuilds.length > 0 && (
          <div className="mb-6">
            <button
              type="button"
              onClick={() => setShowTemp(!showTemp)}
              className="flex items-center gap-2 text-sm font-medium text-[var(--md-graphite)] hover:text-[var(--md-slate)] transition-colors mb-3 normal-case tracking-normal cursor-pointer bg-transparent border-0 p-0"
              style={{ fontFamily: FONT_FAMILY }}
            >
              {showTemp ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              Development Builds ({tempBuilds.length})
              <span className="text-xs font-normal text-[var(--md-slate)] ml-1">
                auto-expire in 7 days
              </span>
            </button>
            {showTemp && (
              <div className="border border-gray-200 rounded-lg p-5">
                <div className="divide-y divide-gray-100">
                  {tempBuilds.map((b) => (
                    <div
                      key={b.version}
                      className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span
                            className="font-medium text-[var(--md-graphite)] text-sm"
                            style={{ fontFamily: FONT_FAMILY }}
                          >
                            v{b.version}
                          </span>
                          <span className="text-[10px] font-mono px-1.5 py-0.5 bg-gray-100 text-[var(--md-slate)] rounded border border-gray-200">
                            {b.commit}
                          </span>
                        </div>
                        <div
                          className="flex items-center gap-3 text-xs text-[var(--md-slate)] mt-1"
                          style={{ fontFamily: FONT_FAMILY }}
                        >
                          <span className="flex items-center gap-1">
                            <GitCommit className="w-3 h-3" />
                            {formatRelativeDate(b.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <HardDrive className="w-3 h-3" />
                            {formatBytes(b.artifact.size)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Expires {formatDate(b.expiresAt)}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => window.location.assign(signUrl(b.artifact.url))}
                        className="inline-flex items-center rounded-full border border-gray-300 px-3 py-1.5 text-xs font-medium text-[var(--md-ink)] hover:bg-[var(--md-fog)] transition-all cursor-pointer bg-white"
                        style={{ fontFamily: FONT_FAMILY }}
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ─── System Requirements ─── */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="border border-gray-200 rounded-lg p-6">
          <h3
            className="text-sm font-medium text-[var(--md-graphite)] mb-3 normal-case tracking-normal flex items-center gap-2"
            style={{ fontFamily: FONT_FAMILY }}
          >
            <Monitor className="w-4 h-4" />
            System Requirements
          </h3>
          <div
            className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-[var(--md-slate)]"
            style={{ fontFamily: FONT_FAMILY }}
          >
            <div>
              <span className="font-medium text-[var(--md-ink)]">Processor</span>
              <p>Apple Silicon (M1, M2, M3, M4)</p>
            </div>
            <div>
              <span className="font-medium text-[var(--md-ink)]">macOS</span>
              <p>11.0 Big Sur or later</p>
            </div>
            <div>
              <span className="font-medium text-[var(--md-ink)]">Disk Space</span>
              <p>~50 MB</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
